import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import PageSEO from '@/components/base/PageSEO';


const aboutTitle = '关于色影视 - 高清免费电影电视剧在线观看平台';
const aboutDesc = '色影视致力于为用户提供高清免费电影、电视剧、综艺的在线观看服务。了解色影视的品牌故事、用户口碑与联系方式，享受超清流畅的观影体验。';
const aboutKeywords = '色影视,关于色影视,影视在线观看平台,免费电影网站,高清影视平台';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: aboutTitle,
  description: aboutDesc,
  url: 'https://www.seyingshi.com/about',
  isPartOf: {
    '@type': 'WebSite',
    name: '色影视',
    url: 'https://www.seyingshi.com/',
  },
};

const orgLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '色影视',
  url: 'https://www.seyingshi.com/',
  description: '高清免费影视在线观看平台，提供电影、电视剧、综艺等海量资源',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'contact@seyingshi.com',
    availableLanguage: ['Chinese'],
  },
};

const faqLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '色影视是否需要注册才能观看？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '完全不需要！色影视所有内容均可免费观看，无需注册、无需登录、无需付费，打开即可畅享高清影视体验。',
      },
    },
    {
      '@type': 'Question',
      name: '色影视的影视资源更新频率如何？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '我们每日更新最新的电影、电视剧和综艺资源，确保您能第一时间观看到热门内容。新剧上线、热片更新，色影视从不缺席。',
      },
    },
    {
      '@type': 'Question',
      name: '在色影视观看视频需要缓冲很久吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '色影视采用先进的视频加速技术，超清画质也能流畅播放，几乎无需等待缓冲。只要您的网络正常，即可享受即点即播的观影体验。',
      },
    },
    {
      '@type': 'Question',
      name: '色影视支持哪些设备观看？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '色影视支持电脑、平板、手机等多种设备在线观看，无需下载任何APP或插件，打开浏览器即可畅享高清影视内容。',
      },
    },
    {
      '@type': 'Question',
      name: '色影视的影视资源涵盖哪些类型？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '色影视涵盖电影、电视剧和综艺三大板块，包括科幻、动作、悬疑、古装、爱情、喜剧、动画、纪录片等多种类型，满足不同用户的观影需求。',
      },
    },
  ],
};

const breadcrumbLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.seyingshi.com/' },
    { '@type': 'ListItem', position: 2, name: '关于我们' },
  ],
};

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <>
      <PageSEO
        title={aboutTitle}
        description={aboutDesc}
        keywords={aboutKeywords}
        canonicalUrl="https://www.seyingshi.com/about"
        jsonLd={[aboutJsonLd, orgLd, faqLd, breadcrumbLd]}
      />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb />

          {/* Hero */}
          <section className="w-full max-w-[1400px] mx-auto px-4 md:px-6 py-12">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 text-center mb-4">
              关于色影视
            </h1>
            <p className="text-foreground-500 text-center max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
              色影视是一个专注于高清免费影视在线观看的平台，我们致力于为每一位用户提供流畅、高清、免费的观影体验。
            </p>
          </section>

          {/* Brand Story */}
          <section className="w-full bg-background-100 py-2">
            <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6 py-12">
              <div className="flex flex-col lg:flex-row gap-10 items-center">
                <div className="flex-1">
                  <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">品牌故事</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    色影视创立于对影视热爱的初心。我们深知，在快节奏的现代生活中，观影是人们放松身心、获取快乐的重要方式。然而，高昂的会员费用和繁琐的注册流程常常让人望而却步。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    因此，我们打造了色影视这个平台 —— <strong>完全免费、无需注册、即点即播</strong>。无论是热映大片、高分剧集还是爆笑综艺，您都可以在这里找到并立即开始观看。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    从最初的几十部影片，到如今涵盖<strong>电影、电视剧、综艺</strong>三大板块的海量资源库，色影视始终坚持"免费高清、流畅不缓冲"的核心理念，为数百万用户提供优质的在线观影服务。
                  </p>
                </div>
                <div className="flex-shrink-0 w-full lg:w-[400px]">
                  <div className="aspect-[4/3] rounded-lg overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=modern%20minimalist%20home%20theater%20setup%20with%20large%20screen%20displaying%20colorful%20movie%20scene%2C%20cozy%20living%20room%20atmosphere%20with%20warm%20ambient%20lighting%2C%20comfortable%20sofa%20with%20blankets%2C%20premium%20home%20cinema%20experience%2C%20soft%20golden%20hour%20light%20through%20window%2C%20cinematic%20lifestyle%20photography&width=800&height=600&seq=about-brand&orientation=landscape"
                      alt="色影视 - 在家畅享高清影视体验"
                      title="色影视 - 高清免费影视在线观看平台"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="w-full max-w-[1400px] mx-auto px-4 md:px-6 py-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { value: '5000+', label: '影视资源' },
                { value: '200万+', label: '活跃用户' },
                { value: '99.9%', label: '播放成功率' },
                { value: '每日更新', label: '内容频率' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-lg bg-background-100">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">{stat.value}</div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Reputation */}
          <section className="w-full bg-background-100 py-2">
            <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6 py-12">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 text-center mb-2">用户口碑</h2>
              <p className="text-sm text-foreground-500 text-center mb-8">听听用户们怎么说</p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    name: '影视爱好者小王',
                    avatar: 'https://readdy.ai/api/search-image?query=professional%20portrait%20photo%20of%20young%20Chinese%20man%20in%20casual%20clothing%2C%20friendly%20smile%2C%20clean%20white%20background%2C%20natural%20lighting%2C%20headshot%20style&width=100&height=100&seq=avatar-01&orientation=squarish',
                    text: '用了色影视大半年了，真的完全免费，画质也超好。之前在其他平台看个电影还要充会员，这里直接就能看，太方便了！',
                    rating: 5,
                  },
                  {
                    name: '追剧达人小李',
                    avatar: 'https://readdy.ai/api/search-image?query=professional%20portrait%20photo%20of%20young%20Chinese%20woman%20smiling%2C%20modern%20casual%20style%2C%20clean%20light%20background%2C%20natural%20daylight%2C%20headshot%20photography&width=100&height=100&seq=avatar-02&orientation=squarish',
                    text: '追剧狂魔在此！色影视的电视剧更新速度真的很快，而且不缓冲不卡顿。每天下班回家追两集，生活美滋滋。',
                    rating: 5,
                  },
                  {
                    name: '电影发烧友老张',
                    avatar: 'https://readdy.ai/api/search-image?query=professional%20portrait%20photo%20of%20middle-aged%20Chinese%20man%20with%20glasses%2C%20friendly%20expression%2C%20clean%20background%2C%20natural%20lighting%2C%20headshot%20style&width=100&height=100&seq=avatar-03&orientation=squarish',
                    text: '资源很全，从经典老片到最新院线电影都有。最重要的是完全不收费，推荐给身边很多朋友了，大家都说好用。',
                    rating: 5,
                  },
                ].map((review) => (
                  <div key={review.name} className="p-6 rounded-lg bg-background-50">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                        <img src={review.avatar} alt={review.name} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-900">{review.name}</div>
                        <div className="flex items-center gap-0.5 mt-0.5">
                          {Array.from({ length: review.rating }).map((_, i) => (
                            <i key={i} className="ri-star-fill text-accent-500 text-xs" />
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">&ldquo;{review.text}&rdquo;</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="w-full max-w-[1400px] mx-auto px-4 md:px-6 py-12">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 text-center mb-2">联系我们</h2>
            <p className="text-sm text-foreground-500 text-center mb-8">有任何问题或建议？欢迎随时联系我们</p>

            <div className="max-w-lg mx-auto">
              <form
                id="contact-form"
                data-readdy-form="true"
                action="https://readdy.ai/api/form/d9njpmcit35ii3jdls9g"
                method="POST"
                className="flex flex-col gap-4"
              >
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                    placeholder="请输入您的留言内容（500字以内）"
                  />
                </div>

                {/* Honeypot anti-spam */}
                <div className="hp-field-wrap">
                  <label htmlFor="phone-alt-contact">备用电话</label>
                  <input type="text" id="phone-alt-contact" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>

                <button
                  type="submit"
                  className="px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  发送留言
                </button>
              </form>

              <div className="mt-8 pt-8 border-t border-background-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-mail-line text-lg" />
                    </div>
                    <div>
                      <div className="text-xs text-foreground-400">邮箱</div>
                      <div className="text-sm text-foreground-700">contact@seyingshi.com</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                      <i className="ri-time-line text-lg" />
                    </div>
                    <div>
                      <div className="text-xs text-foreground-400">工作时间</div>
                      <div className="text-sm text-foreground-700">周一至周日 9:00 - 22:00</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}